/**
 * Bringing a picture into a drawing.
 *
 * ## The asset story, and why it is a data URI
 *
 * This was deferred through two milestones for one reason: an image needs somewhere to live,
 * and every obvious answer is wrong for this application.
 *
 * - A **blob URL** is valid only inside the tab that made it. A document referencing one is
 *   broken the moment it is saved, reloaded, emailed, or opened tomorrow — and it looks fine
 *   until then, which is the worst possible failure shape for a teacher's lesson.
 * - A **file path** assumes a filesystem the browser cannot read and the recipient does not
 *   share.
 * - A **server** is an entire piece of infrastructure this application does not have and does
 *   not want: EduSketch runs from a static file and stores documents in the browser.
 *
 * A `data:` URI is the only option under which a document remains **one self-contained
 * artefact**. The document is JSON, the image is text inside it, and the file that leaves the
 * browser is complete. That is the property everything else in this codebase is arranged
 * around — a worksheet emailed to a colleague opens as the same drawing.
 *
 * ## The price, stated plainly
 *
 * Base64 is a third larger than the bytes it encodes, and `localStorage` — where autosave
 * lives — holds a few megabytes in total. A photograph would fill it and take the whole
 * document down with it, so the limit below is enforced at import rather than discovered at
 * save. Refusing a file with a reason beats accepting it and losing the drawing later.
 *
 * The limit is deliberately generous for what this is *for*: a scanned figure, a textbook
 * diagram, a photographed sketch to trace over.
 */

/** The most a single image may contribute, in bytes of source file. */
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const ACCEPTED = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml']);

export interface ImportedImage {
  /** A `data:` URI holding the whole picture. */
  readonly href: string;
  /** Natural size in pixels, used to give the placed image a sensible aspect ratio. */
  readonly width: number;
  readonly height: number;
}

export class ImageImportError extends Error {}

/** Injected so the import can be tested without a DOM. */
export interface ImageImportHost {
  readAsDataUrl(file: Blob): Promise<string>;
  measure(href: string): Promise<{ readonly width: number; readonly height: number }>;
}

export function browserImageHost(): ImageImportHost {
  return {
    readAsDataUrl: (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          resolve(typeof reader.result === 'string' ? reader.result : '');
        });
        reader.addEventListener('error', () => {
          reject(new ImageImportError('không đọc được tệp'));
        });
        reader.readAsDataURL(file);
      }),

    measure: (href) =>
      new Promise((resolve, reject) => {
        const image = new globalThis.Image();
        image.addEventListener('load', () => {
          resolve({ width: image.naturalWidth, height: image.naturalHeight });
        });
        image.addEventListener('error', () => {
          reject(new ImageImportError('tệp này không phải là ảnh đọc được'));
        });
        image.src = href;
      }),
  };
}

/**
 * Reads a picked file into something a document can hold.
 *
 * Every rejection carries a reason in the interface language, because "nothing happened" is
 * the one response that gives the user no way forward.
 */
export async function importImage(
  file: File,
  host: ImageImportHost = browserImageHost()
): Promise<ImportedImage> {
  if (!ACCEPTED.has(file.type)) {
    throw new ImageImportError(`không hỗ trợ định dạng ${file.type || 'này'}`);
  }

  if (file.size > MAX_IMAGE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    const limit = (MAX_IMAGE_BYTES / 1024 / 1024).toFixed(0);
    throw new ImageImportError(`ảnh ${mb} MB vượt quá giới hạn ${limit} MB`);
  }

  const href = await host.readAsDataUrl(file);
  if (!href.startsWith('data:')) throw new ImageImportError('không đọc được tệp');

  const { width, height } = await host.measure(href);
  if (width <= 0 || height <= 0) throw new ImageImportError('ảnh không có kích thước');

  return { href, width, height };
}

/**
 * How large to place an image, in world units.
 *
 * Fitted into a fixed box rather than placed at its pixel size. A phone photograph is four
 * thousand pixels across and would arrive as an object far larger than the entire drawing,
 * leaving the user zoomed inside a wall of colour with no idea what happened. Fitting keeps
 * the aspect ratio and lands the picture at a size that is visibly *an object on the canvas*.
 */
export function placementSize(
  natural: { readonly width: number; readonly height: number },
  fit = 300
): { readonly width: number; readonly height: number } {
  const longest = Math.max(natural.width, natural.height);
  if (longest <= 0) return { width: fit, height: fit };

  const factor = fit / longest;
  return { width: natural.width * factor, height: natural.height * factor };
}
